/**
 * Phase 7-lite — PWA install platform detection + native prompt manager.
 *
 * Centralises:
 *  - iOS / iPad / Android / desktop detection
 *  - iOS major version extraction (PWA push requires 16.4+)
 *  - Standalone (already installed) detection
 *  - Capturing the `beforeinstallprompt` event so we can fire native
 *    install on Android Chrome / desktop Chrome / Edge
 *  - Referral-code preservation across accidental redirects via
 *    sessionStorage so a mis-tap that bounces a user to a clean /register
 *    URL doesn't lose the QR's `?ref=ABC` parameter.
 */

const REF_KEY = '_pending_ref_code';
const PREVIEW_INSTALL_KEY = '_preview_install_mode';

let _deferredPrompt = null;
let _bipFired = false;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _bipFired = true;
    // Lets components re-render
    window.dispatchEvent(new Event('pwa-install-available'));
  });
  window.addEventListener('appinstalled', () => {
    _deferredPrompt = null;
    window.dispatchEvent(new Event('pwa-installed'));
  });
}

export function detectPlatform() {
  if (typeof window === 'undefined') {
    return { isIOS: false, isIPad: false, isAndroid: false, isDesktop: false, iosMajor: 0, isStandalone: false };
  }
  const ua = window.navigator.userAgent || '';
  const isIPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const isIPhone = /iPhone|iPod/.test(ua) && !window.MSStream;
  const isIOS = isIPad || isIPhone;
  const isAndroid = /Android/.test(ua);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  let iosMajor = 0;
  const m = ua.match(/OS (\d+)[_\.](\d+)/);
  if (m) iosMajor = parseInt(m[1], 10);
  const isDesktop = !isIOS && !isAndroid;
  return { isIOS, isIPad, isIPhone, isAndroid, isDesktop, iosMajor, isStandalone };
}

export function canPromptInstall() {
  return Boolean(_deferredPrompt);
}

export function hasNativeInstallEverFired() {
  return _bipFired;
}

/** Triggers Chrome/Edge native install dialog. Returns the user's choice. */
export async function promptInstall() {
  if (!_deferredPrompt) return { outcome: 'unavailable' };
  try {
    _deferredPrompt.prompt();
    const choice = await _deferredPrompt.userChoice;
    _deferredPrompt = null;
    return choice; // { outcome: 'accepted' | 'dismissed' }
  } catch (err) {
    return { outcome: 'error', error: String(err) };
  }
}

// ─────────────────────────────────────────────────────────────────────
// Referral-code preservation
// Scans URL for ?ref= / ?staff= on every page load and stores in
// sessionStorage. RegisterPage reads it as a fallback when URL params
// are missing (e.g. user mis-tapped a banner and got redirected).
// ─────────────────────────────────────────────────────────────────────
export function capturePendingRef() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || params.get('staff') || '').trim().toUpperCase();
    if (ref) {
      window.sessionStorage.setItem(REF_KEY, ref);
    }
  } catch (_) { /* sessionStorage may be blocked in private mode */ }
}

export function getPendingRef() {
  if (typeof window === 'undefined') return '';
  try {
    return (window.sessionStorage.getItem(REF_KEY) || '').toUpperCase();
  } catch (_) { return ''; }
}

export function clearPendingRef() {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(REF_KEY); } catch (_) { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────────────
// Preview-install mode
// When the URL contains `?preview_install=1` we stash a sessionStorage
// flag that forces the install pill + walkthrough to render even inside
// the installed PWA. Lets operators demo the install flow to customers
// or preview design changes without uninstalling first. Auto-clears
// when the tab closes (sessionStorage scope).
// ─────────────────────────────────────────────────────────────────────
export function capturePreviewInstall() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview_install') === '1') {
      window.sessionStorage.setItem(PREVIEW_INSTALL_KEY, '1');
    }
  } catch (_) { /* sessionStorage blocked */ }
}

export function isPreviewInstallMode() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(PREVIEW_INSTALL_KEY) === '1';
  } catch (_) { return false; }
}
